import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import {
  rangeOf,
  rangeTitle,
  step,
  workingDays,
  startOfDay,
  ymd,
  type CalendarView,
} from "../../lib/calendar";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import {
  TONE_LABELS,
  whoFilter,
  type MeetingsViewState,
} from "../../lib/meetings-view";
import type { SalesMeeting } from "../../lib/sales-types";
import type { User } from "../../types";
import { cn } from "../ui/cn";
import { MonthGrid } from "./MonthGrid";
import { TimeGrid } from "./TimeGrid";

/**
 * The calendar: Google Calendar's day, week and month, over Pulse's diary.
 *
 * The header is Google's too -- Today, back and forward, the range as a title,
 * and the view switch -- and so are the keys: D, W and M for the views, T for
 * today, J/K or the arrow keys to step. Nobody should have to learn a second
 * calendar to use this one.
 *
 * It asks the server for exactly the window on screen (`when=range`), whole,
 * so the grid can never be missing a meeting because a page boundary fell on a
 * Thursday.
 */
export function MeetingsCalendar({
  user,
  view,
  state,
  set,
  selectedId,
  onOpen,
  onCreate,
}: {
  user: User;
  view: CalendarView;
  state: MeetingsViewState;
  set: (patch: Record<string, string | null>) => void;
  selectedId: number | null;
  onOpen: (meeting: SalesMeeting, anchor: DOMRect) => void;
  onCreate: (start: Date, minutes: number) => void;
}) {
  const client = useQueryClient();
  const now = useNow();
  const { days, from, to } = rangeOf(view, state.date);

  const params = {
    when: "range" as const,
    from: from.toISOString(),
    to: to.toISOString(),
    ...whoFilter(state.who),
  };

  const meetings = useQuery({
    queryKey: queryKeys.salesMeetings(params),
    queryFn: () => salesApi.meetings(params),
    staleTime: 30_000,
    /* Keep the last range on screen while the next loads, so stepping a week
       does not flash an empty grid. */
    placeholderData: (previous) => previous,
  });

  const move = useMutation({
    mutationFn: ({ meeting, start, end }: { meeting: SalesMeeting; start: Date; end: Date }) =>
      salesApi.updateMeeting(meeting.id, {
        startAt: start.toISOString(),
        /* A meeting booked as open-ended stays open-ended when it is moved;
           only a resize gives it an end. */
        endAt:
          meeting.endAt || end.getTime() - start.getTime() !== 60 * 60_000
            ? end.toISOString()
            : null,
      }),
    onSettled: () => void client.invalidateQueries({ queryKey: queryKeys.salesRoot }),
  });

  const go = (patch: { view?: CalendarView; date?: Date }): void =>
    set({
      ...(patch.view ? { view: patch.view } : {}),
      ...(patch.date ? { date: sameAsToday(patch.date) ? null : ymd(patch.date) } : {}),
    });

  /* Google's keys. Ignored while typing, and with any modifier held so the
     browser's own shortcuts keep working. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;
      if (document.querySelector("[role=dialog]")) return;

      const key = event.key.toLowerCase();
      if (key === "d") go({ view: "day" });
      else if (key === "w") go({ view: "week" });
      else if (key === "m") go({ view: "month" });
      else if (key === "t") go({ date: new Date() });
      else if (key === "j" || key === "arrowright") go({ date: step(view, state.date, 1) });
      else if (key === "k" || key === "arrowleft") go({ date: step(view, state.date, -1) });
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const rows = meetings.data?.meetings ?? [];
  /* Weekdays only, unless the weekend has something on -- see `workingDays`.
     The day view shows whatever day it is on, Saturday included. */
  const shown = view === "day" ? days : workingDays(days, rows);

  return (
    /*
      `isolate` keeps the grid's own layering -- the sticky day headings, the
      now line, a block being dragged -- inside the calendar. Without it those
      z-indexes competed with the app's, and the sticky headings were drawn
      over the account menu, hiding Your settings.
    */
    <div className="isolate flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-1.5 border-b border-line px-2 py-2 sm:gap-2 sm:px-3">
        <button
          type="button"
          onClick={() => go({ date: new Date() })}
          title="Today (T)"
          className="h-8 rounded-md border border-line px-2.5 text-xs text-content hover:bg-surface-2 sm:px-3 sm:text-sm"
        >
          Today
        </button>
        <div className="flex">
          <IconButton label="Previous (K)" onClick={() => go({ date: step(view, state.date, -1) })}>
            <path d="M15 18l-6-6 6-6" />
          </IconButton>
          <IconButton label="Next (J)" onClick={() => go({ date: step(view, state.date, 1) })}>
            <path d="M9 18l6-6-6-6" />
          </IconButton>
        </div>
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium text-content sm:text-lg">
          <span className="sm:hidden">{rangeTitle(view, state.date, true)}</span>
          <span className="hidden sm:inline">{rangeTitle(view, state.date)}</span>
        </h2>

        {meetings.isFetching ? <span className="text-[11px] text-faint">Loading…</span> : null}

        <div className="flex overflow-hidden rounded-md border border-line" role="group" aria-label="View">
          {(["day", "week", "month"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              title={`${option[0]!.toUpperCase()}${option.slice(1)} (${option[0]!.toUpperCase()})`}
              onClick={() => go({ view: option })}
              className={cn(
                "h-8 px-2 text-xs capitalize sm:px-3",
                view === option
                  ? "bg-brand-600 font-medium text-white"
                  : "bg-surface text-muted hover:bg-surface-2",
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {move.isError ? (
        <p className="border-b border-line bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {move.error instanceof ApiError ? move.error.message : "Could not move that meeting."}
        </p>
      ) : null}

      <div className="min-h-0 flex-1">
        {view === "month" ? (
          <MonthGrid
            days={shown}
            month={state.date}
            meetings={rows}
            now={now}
            selectedId={selectedId}
            onOpen={onOpen}
            onPickWeek={(day) => go({ view: "week", date: day })}
            onCreate={onCreate}
          />
        ) : (
          <TimeGrid
            days={shown}
            meetings={rows}
            now={now}
            selectedId={selectedId}
            canDrag={(meeting) => canChange(meeting, user)}
            onOpen={onOpen}
            onCreate={onCreate}
            onMove={(meeting, start, end) => move.mutateAsync({ meeting, start, end })}
            onPickDay={(day) => go({ view: "day", date: day })}
          />
        )}
      </div>

      {/* What the colours mean, said once. */}
      <div className="hidden flex-wrap gap-x-4 gap-y-1 border-t border-line px-3 py-1.5 text-[11px] text-faint sm:flex">
        {(Object.keys(TONE_LABELS) as (keyof typeof TONE_LABELS)[]).map((tone) => (
          <span key={tone} className="flex items-center gap-1.5">
            <span
              className={cn(
                "size-2.5 rounded-sm",
                tone === "visit" ? "bg-sky-600" : tone === "call" ? "bg-violet-600" : "bg-emerald-600",
              )}
            />
            {TONE_LABELS[tone]}
          </span>
        ))}
        <span className="ml-auto">Drag to book · drag a meeting to move it · D W M T J K</span>
      </div>
    </div>
  );
}

/**
 * Whether this person may move a meeting -- the server's rule, mirrored so the
 * grid does not offer a drag it will refuse. Somebody going, a manager, or a
 * meeting with nobody on it.
 */
export function canChange(meeting: SalesMeeting, user: User): boolean {
  if (meeting.status !== "scheduled") return false;
  if (user.role === "admin") return true;
  if (meeting.attendees.length === 0) return true;
  return meeting.attendees.some((person) => person.userId === user.id);
}

/** The time now, ticking each minute so the red line moves. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function sameAsToday(date: Date): boolean {
  return startOfDay(date).getTime() === startOfDay(new Date()).getTime();
}

export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex size-8 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-content"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {children}
      </svg>
    </button>
  );
}
